import React, { useState, useEffect } from 'react';
import './OrderInfo.css';
import Header from '../../components/Header/Header';
import NavInf from '../../components/NavInf/NavInf';
import Footer from '../../components/Footer/Footer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronLeft, faMoneyBill, faShop, faUser, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { useParams } from 'react-router-dom';
import secureAxios from '../../utils/secureAxios';
import { secureFetch } from '../../utils/secureFetch';
import Alert from '../../components/Alert/Alert';
import Loader from '../../components/Loader/Loader';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase';
import ShareButton from '../../components/ShareButton/ShareButton';
import { useNavigate } from 'react-router-dom';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { pdf } from '@react-pdf/renderer';
import OrderPDF from '../../components/OrderPDF/OrderPDF';

const statusTranslations = {
    pending: "Pendiente",
    processing: "En proceso",
    shipped: "Enviado",
    delivered: "Entregado",
    canceled: "Cancelado"
};

const OrderInfo = () => {
    const { uuid } = useParams();
    const [order, setOrder] = useState(null);
    const [error, setError] = useState(null);
    const [alertConfig, setAlertConfig] = useState(null);
    const [showCustomerDetails, setShowCustomerDetails] = useState(false);
    const [showProviderDetails, setShowProviderDetails] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(null);
    const [pdfGenerating, setPdfGenerating] = useState(false);
    const navigate = useNavigate();

    const user = JSON.parse(localStorage.getItem('usuario'));

    useEffect(() => {
        const fetchOrder = async () => {
            try {
                const { data } = await secureAxios.get(`/orders/${uuid}`);
                setOrder(data);
            } catch (err) {
                console.error('Error al obtener la orden:', err);
                setError('Error al cargar la orden.');
            }
        };

        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setIsLoggedIn(true);
                fetchOrder();
            } else {
                setIsLoggedIn(false);
                setError('Debes iniciar sesión para ver la orden.');
            }
        });

        return () => unsubscribe();
    }, [uuid]);

    const isProviderOfOrder = user?.proveedorInfo?.id && order?.provider?.id === user.proveedorInfo.id;

    // Función para convertir WebP a PNG
    const convertWebPtoPNG = async (webpUrl) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.src = webpUrl;

        await new Promise((res, rej) => {
            img.onload = res;
            img.onerror = rej;
        });

        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);

        return canvas.toDataURL('image/png');
    };

    // Función para generar y descargar PDF on-demand
    const handleDownload = async () => {
        if (!order) {
            setAlertConfig({ type: 'error', message: 'No hay datos de la orden disponibles.' });
            return;
        }

        try {
            setPdfGenerating(true);

            // Convertir logo si existe
            const logoWebp = order?.provider?.logo_url;
            const logoPng = logoWebp ? await convertWebPtoPNG(logoWebp) : null;

            // Crear el documento PDF
            const doc = (
                <OrderPDF
                    content={{
                        logo: logoPng,
                        nombre_empresa: order?.provider?.nombre_empresa || '',
                        nombre_cliente: order.customer?.nombre,
                        celular: order.customer?.celular,
                        direccion: order.customer?.direccion,
                        notas: order?.notes,
                        fecha: new Date(order?.createdAt).toLocaleDateString('es-CO'),
                        orden_num: order?.providerOrderNumber || '1001',
                        total: order?.totalPrice,
                        items: order.items.map((item) => ({
                            nombre_producto: item.productName,
                            referencia: item.reference,
                            cantidad: item.quantity,
                            precio: item.unitPrice,
                            talla: item.size,
                            color: item.color,
                            imagen: null,
                        })),
                    }}
                />
            );

            // Generar PDF como blob
            const blob = await pdf(doc).toBlob();

            // Crear enlace de descarga
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `pedido-${order.id}-${order.provider?.nombre_empresa?.replace(/\s+/g, '_') || 'orden'}.pdf`;
            
            // Simular click para descargar
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Limpiar URL
            URL.revokeObjectURL(url);

            setAlertConfig({ type: 'success', message: 'PDF descargado exitosamente.' });

        } catch (error) {
            console.error('Error generando PDF:', error);
            setAlertConfig({ type: 'error', message: 'Error al generar el PDF. Intenta nuevamente.' });
        } finally {
            setPdfGenerating(false);
        }
    };

    if (error) {
        return (
            <>
                <Header minimal={true} searchBar={true} backOn={true} />
                <div className="order-container">
                    <Alert message={error} type="error" onClose={() => navigate('/login')} />
                </div>
                <NavInf />
                <Footer />
            </>
        );
    }

    if (!order && isLoggedIn) {
        return (
            <>
                <Header minimal={true} searchBar={true} backOn={true} />
                <Loader />
                <NavInf />
                <Footer />
            </>
        );
    }



    if (!order) {
        return (
            <>
                <Header minimal={true} searchBar={true} backOn={true} />
                <Loader />
                <NavInf />
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header minimal={true} searchBar={true} backOn={true} />
            {alertConfig && (
                <Alert type={alertConfig.type} message={alertConfig.message} onClose={() => setAlertConfig(null)} />
            )}
            <div className="order-container">
                <div className='order-header'>
                    <h2 className='order-num'>Orden #{order.providerOrderNumber}</h2>
                    <a
                        className='order-whatsapp'
                        href="/"
                        onClick={(e) => {
                            e.preventDefault();
                            if (!order) return;

                            const id = order.id;
                            const key = `whatsapp_sent_order_${id}`;
                            const yaSeEnvio = localStorage.getItem(key) === 'true';
                            const isProvider = isProviderOfOrder;

                            let telefono = '';

                            if (isProvider) {
                                // Proveedor quiere contactar al cliente
                                telefono = order.customer?.celular || '';
                            } else {
                                // Cliente quiere contactar al proveedor
                                telefono = order.provider?.usuario?.telefono || '';
                            }


                            if (!telefono) return;

                            const numero = telefono.replace(/\D/g, '');
                            const baseURL = `https://wa.me/${numero}`;

                            if (yaSeEnvio) {
                                window.open(baseURL, '_blank');
                                return;
                            }

                            const nombreEmpresa = order.provider?.nombre_empresa || 'Minymol';
                            const ciudad = order.customer?.ciudad || '';
                            const departamento = order.customer?.departamento || '';
                            const direccion = order.customer?.direccion || '';
                            const nombreCliente = order.customer?.nombre || '';
                            const total = Number(order.totalPrice || 0).toLocaleString('es-CO');
                            const cantidad = Array.isArray(order.items)
                                ? order.items.reduce((acc, item) => acc + item.quantity, 0)
                                : 0;

                            const mensaje = isProvider
                                ? `Hola! Te saludamos desde ${nombreEmpresa}. Hemos recibido tu pedido en Minymol con la siguiente información:\n\nPedido No. ${id}\nNombre del cliente: ${nombreCliente}\nDestino: ${direccion}, ${ciudad}, ${departamento}\nTotal: $${total}\n\n¿Me confirmas sin los datos son correctos?`
                                : `Hola! Acabo de hacer un pedido desde Minymol.\n\nPedido N.º: ${id}\nProductos: ${cantidad}\nTotal: $${total}\nEnvío: ${direccion}, ${ciudad}, ${departamento}\nCliente: ${nombreCliente}\n\n¿Cómo es el método de pago y envío?`;

                            localStorage.setItem(key, 'true');
                            const urlFinal = `${baseURL}?text=${encodeURIComponent(mensaje)}`;
                            window.open(urlFinal, '_blank');
                        }}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <FontAwesomeIcon icon={faWhatsapp} />
                        <span>Contactar</span>
                    </a>
                    <button 
                        onClick={handleDownload} 
                        className="download-pdf" 
                        disabled={pdfGenerating}
                        style={{ opacity: pdfGenerating ? 0.6 : 1 }}
                    >
                        <FontAwesomeIcon icon={faFilePdf} />
                        {pdfGenerating && <span style={{ marginLeft: '5px', fontSize: '12px' }}>...</span>}
                    </button>
                </div>
                <div className='line order'></div>

                <div className='order-status'>
                    <div className='info-status'>
                        <p><strong>Estado:</strong></p>
                        {isProviderOfOrder ? (
                            <select
                                value={order.status}
                                onChange={async (e) => {
                                    try {
                                        const newStatus = e.target.value;
                                        const response = await secureFetch(`https://api.minymol.com/orders/${order.id}/status`, {
                                            method: 'PUT',
                                            headers: {
                                                'Content-Type': 'application/json',
                                            },
                                            body: JSON.stringify({ status: newStatus })
                                        });

                                        if (!response.ok) throw new Error('Error al actualizar');

                                        const updated = await response.json();
                                        setOrder({ ...order, status: updated.status });
                                        setAlertConfig({ type: 'success', message: 'Estado actualizado correctamente' });
                                    } catch (err) {
                                        console.error(err);
                                        setAlertConfig({ type: 'error', message: 'Error al actualizar el estado de la orden' });
                                    }
                                }}
                            >
                                {Object.entries({
                                    pending: "Pendiente",
                                    processing: "En proceso",
                                    shipped: "Enviado",
                                    delivered: "Entregado",
                                    canceled: "Cancelado"
                                }).map(([value, label]) => (
                                    <option
                                        key={value}
                                        value={value}
                                    >
                                        {label}
                                    </option>
                                ))}
                            </select>

                        ) : (
                            <p><strong>●</strong> {statusTranslations[order.status] || order.status}</p>
                        )}
                    </div>
                    <p className='date-update'><strong>Última Actualización:</strong> {new Date(order.updatedAt).toLocaleString()}</p>
                </div>

                <div className='line'></div>

                <div className="order-details-wrapper">
                    <button className="toggle-button" onClick={() => setShowCustomerDetails(!showCustomerDetails)}>
                        <div>
                            <FontAwesomeIcon icon={faUser} />
                            <p>{order.customer?.nombre || 'Cliente'}</p>
                        </div>
                        <FontAwesomeIcon className='icon-details' icon={showCustomerDetails ? faChevronDown : faChevronLeft} />
                    </button>

                    <div className={`order-details ${showCustomerDetails ? 'open' : 'closed'}`}>
                        {order.customer ? (
                            <>
                                <p>{order.customer?.direccion || 'Sin direccion'}</p>
                                <p>{order.customer?.ciudad || 'Bogotá'}</p>
                                <p>{order.customer?.departamento || 'Santander'}</p>
                            </>
                        ) : (
                            <p>Dirección no registrada</p>
                        )}
                        <p>{order.customer?.celular || 'No tiene'}</p>
                    </div>
                </div>

                <div className='space'></div>

                <div className="order-items">
                    {Object.values(
                        order.items.reduce((acc, item) => {
                            const key = `${item.productId}_${item.color || 'sin-color'}`;

                            if (!acc[key]) {
                                acc[key] = {
                                    ...item,
                                    variants: [],
                                    totalQuantity: 0,
                                    subtotal: 0,
                                };
                            }

                            acc[key].variants.push({
                                size: item.size,
                                quantity: item.quantity,
                            });

                            acc[key].totalQuantity += item.quantity;
                            acc[key].subtotal += item.unitPrice * item.quantity;

                            return acc;
                        }, {})
                    ).map((groupedItem, index) => (
                        <div key={index} className="order-item-card">
                            <div className='item-order-info-cont'>
                                <img src={groupedItem.imageSnapshot} alt={groupedItem.productName} />
                                <div className="item-info-cont">
                                    <p>{groupedItem.productName}</p>
                                    <div className="item-order-info">
                                        {groupedItem.color && (
                                            <p><strong>Color:</strong> {groupedItem.color}</p>
                                        )}
                                        <p>
                                            x{groupedItem.totalQuantity} {groupedItem.unity === 'unidad' ? 'unidades' : groupedItem.unity === 'docena' ? 'docenas' : groupedItem.unity}
                                        </p>
                                    </div>
                                    <p><strong>Subtotal:</strong> {Number(groupedItem.subtotal).toLocaleString('es-CO', { maximumFractionDigits: 0 }) || '—'} </p>
                                </div>
                            </div>
                            <div className="info-variants-bar">
                                {[...groupedItem.variants]
                                    .sort((a, b) => {
                                        const sizeA = parseFloat(a.size) || 0;
                                        const sizeB = parseFloat(b.size) || 0;
                                        return sizeA - sizeB;
                                    })
                                    .map((v, i) => (
                                        <span key={i} className="fraction">
                                            <span className="top">{v.size || '-'}</span>
                                            <span className="bottom">{v.quantity}</span>
                                        </span>
                                    ))}
                            </div>
                        </div>
                    ))}
                </div>

                <div className='space'></div>

                <div className='order-total-price'>
                    <div>
                        <FontAwesomeIcon icon={faMoneyBill} />
                        <p>Monto total a pagar</p>
                    </div>
                    <p><strong>{Number(order.totalPrice).toLocaleString('es-CO', { maximumFractionDigits: 0 }) || '—'}</strong></p>
                </div>

                <div className="order-details-wrapper">
                    <button className="toggle-button" onClick={() => setShowProviderDetails(!showProviderDetails)}>
                        <div>
                            <FontAwesomeIcon icon={faShop} />
                            <p>{order.provider?.nombre_empresa || 'Proveedor'}</p>
                        </div>
                        <FontAwesomeIcon className='icon-details' icon={showProviderDetails ? faChevronDown : faChevronLeft} />
                    </button>

                    <div className={`order-details ${showProviderDetails ? 'open' : 'closed'}`}>
                        <p><strong>Calificación:</strong> {order.provider.calificacion}</p>
                    </div>
                </div>

                <div className='space'></div>
            </div>
            <ShareButton
                title={`Orden #${order.id} - ${order.provider?.nombre_empresa || 'Proveedor'}`}
                imageUrl={order.items[0]?.imageSnapshot || 'https://cdn.minymol.com/uploads/logoblanco.webp'}
                redirectUrl={`https://minymol.com/orden/${order.id}`}
            />
            <NavInf />
            <Footer />
        </>
    );
};

export default OrderInfo;
